import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Toggle } from '../../../ui/Toggle/Toggle';
import { SearchSection } from '../../SearchSection/SearchSection';
import { UsersTable } from '../UsersTable/UsersTable';
import {
  setUsersOrganizationFilter,
  setUsersQuery,
  setUsersType
} from '../../../../store/slices/userManagementSlice';
import { useAppSelector } from '../../../../hooks/redux';
import { selectIsSuperAdmin } from '../../../../store/selectors/authSelectors';
import { getOrganizations } from '../../../../services/OrganizationService';
import { EmptyState } from '../../../ui/EmptyState/EmptyState';
import { FilterDropdown } from '../../../ui/FilterDropdown/FilterDropdown';
import SearchIcon from '../../../../assets/search_icon.svg';

export function UserOverview() {
  const dispatch = useDispatch();
  const { users, usersLoading, usersFilters } = useAppSelector(
    (state) => state.userManagement
  );
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);

  const [searchValue, setSearchValue] = useState(usersFilters.q);
  const [organizationOptions, setOrganizationOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);

  // Searching is a server round trip now, so don't fire one per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchValue !== usersFilters.q) {
        dispatch(setUsersQuery(searchValue));
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [dispatch, searchValue, usersFilters.q]);

  // Only a superadmin can filter by organization: an admin would get a
  // single-element list back, and a USER gets 403 CROSS_ORG_ACCESS_DENIED.
  useEffect(() => {
    if (!isSuperAdmin) return;

    const loadOrganizations = async () => {
      setOrganizationsLoading(true);
      try {
        const response = await getOrganizations();
        setOrganizationOptions(
          response.data.items.map((organization) => ({
            value: organization.id,
            label: organization.name
          }))
        );
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
        setOrganizationOptions([]);
      } finally {
        setOrganizationsLoading(false);
      }
    };

    loadOrganizations();
  }, [isSuperAdmin]);

  const onlyShowInvitedUsers = usersFilters.type === 'INVITED';
  const hasNoResults = !usersLoading && users !== null && users.total === 0;
  const hasActiveCriteria =
    searchValue.length > 0 ||
    onlyShowInvitedUsers ||
    usersFilters.organizationId.length > 0;

  return (
    <div className="flex flex-col w-full gap-4 justify-between flex-1 h-full">
      <div className="flex items-center gap-2 w-full">
        <div className="flex-1 min-w-0">
          <SearchSection
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            placeholder="Search users by name"
          />
        </div>
        {isSuperAdmin && (
          <div className="w-64 shrink-0">
            <FilterDropdown
              placeholder={organizationsLoading ? 'Loading...' : 'Organization'}
              options={organizationOptions}
              selectedValues={usersFilters.organizationId}
              onSelectionChange={(values: string[]) =>
                dispatch(setUsersOrganizationFilter(values))
              }
              className="w-full"
            />
          </div>
        )}
      </div>
      <Toggle
        label="Only show invited users"
        checked={onlyShowInvitedUsers}
        onChange={() =>
          dispatch(setUsersType(onlyShowInvitedUsers ? 'ALL' : 'INVITED'))
        }
        checkedBgColor="bg-purple-700"
      />

      {hasNoResults && hasActiveCriteria ? (
        <EmptyState
          icon={<img src={SearchIcon} alt="No data" className="w-9 h-9" />}
          title="No Users matched your criteria."
          description="Try adjusting your search."
        />
      ) : (
        <div className="flex-1 overflow-auto max-h-[calc(100vh-256px)]">
          <UsersTable />
        </div>
      )}
    </div>
  );
}
